import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { centsToDollars, dollarsToCents } from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'

import { getFetchError } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'

import {
  Breadcrumb,
  Heading,
  HeadingVariants,
  Icon,
  Alert,
  Card,
  Form,
  RadioGroupItem,
} from '@/ui'
import { PracticeProfileFormCta } from './PracticeProfileFormCta'

// TODO: Replace Formik with New UI skipcq: JS-0099
import { getFieldName } from '@/base'
import FileUploadFormInput from '@/componentsV1/FileUpload/FileUploadFormInput'
import {
  FILING_STATUSES,
  FilingStatusOptions,
  TAX_ENTITY_TYPES,
  TaxEntityTypeOptions,
} from '@/featuresV1/Taxes/taxConstants'
import { fetchUserDocumentCategoriesIfNeeded } from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategories.slice'
import { UserDocumentCategoryIdentifier } from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategory.constants'

export const shouldDisplaySpouse = (
  filingStatus: FILING_STATUSES | null | undefined
) => Boolean(filingStatus && ['married_filing_jointly'].includes(filingStatus))

export const TaxInformationForm = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const taxYear = DateTime.now().year
  const fp = useReselector(getFinancialProfile)
  const error = useReselector(
    getFetchError,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id)
  )

  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
  }, [dispatch])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      filingStatus: fp?.filingStatus,
      taxEntityType: fp?.taxEntityType,
      itemizedDeduction: fp?.itemizedDeduction,
      numberOfDependents: fp?.numberOfDependents,
      wIncomeInCents: fp?.wIncomeInCents && centsToDollars(fp?.wIncomeInCents),
      scorpPracticeWIncomeInCents:
        fp?.scorpPracticeWIncomeInCents &&
        centsToDollars(fp?.scorpPracticeWIncomeInCents),
      federalWithholdingInCents:
        fp?.federalWithholdingInCents &&
        centsToDollars(fp?.federalWithholdingInCents),
      stateWithholdingInCents:
        fp?.stateWithholdingInCents &&
        centsToDollars(fp?.stateWithholdingInCents),
      otherIndividualIncomeInCents:
        fp?.otherIndividualIncomeInCents &&
        centsToDollars(fp?.otherIndividualIncomeInCents),
      spouseFirstName: fp?.spouseFirstName,
      spouseLastName: fp?.spouseLastName,
      spouseWIncomeInCents:
        fp?.spouseWIncomeInCents && centsToDollars(fp?.spouseWIncomeInCents),
      spouseFederalWithholdingInCents:
        fp?.spouseFederalWithholdingInCents &&
        centsToDollars(fp?.spouseFederalWithholdingInCents),
      spouseStateWithholdingInCents:
        fp?.spouseStateWithholdingInCents &&
        centsToDollars(fp?.spouseStateWithholdingInCents),
      spouseIndividualIncomeInCents:
        fp?.spouseIndividualIncomeInCents &&
        centsToDollars(fp?.spouseIndividualIncomeInCents),
    },

    onSubmit: async ({
      filingStatus,
      taxEntityType,
      itemizedDeduction,
      numberOfDependents,
      wIncomeInCents,
      scorpPracticeWIncomeInCents,
      federalWithholdingInCents,
      stateWithholdingInCents,
      otherIndividualIncomeInCents,
      spouseFirstName,
      spouseLastName,
      spouseWIncomeInCents,
      spouseFederalWithholdingInCents,
      spouseStateWithholdingInCents,
      spouseIndividualIncomeInCents,
    }) => {
      const res = await dispatch(
        updateFinancialProfile(fp?.id, {
          filingStatus,
          taxEntityType,
          itemizedDeduction,
          numberOfDependents,
          wIncomeInCents: dollarsToCents(wIncomeInCents),
          scorpPracticeWIncomeInCents: dollarsToCents(
            scorpPracticeWIncomeInCents
          ),
          federalWithholdingInCents: dollarsToCents(federalWithholdingInCents),
          stateWithholdingInCents: dollarsToCents(stateWithholdingInCents),
          otherIndividualIncomeInCents: dollarsToCents(
            otherIndividualIncomeInCents
          ),
          spouseFirstName,
          spouseLastName,
          spouseWIncomeInCents: dollarsToCents(spouseWIncomeInCents),
          spouseFederalWithholdingInCents: dollarsToCents(
            spouseFederalWithholdingInCents
          ),
          spouseStateWithholdingInCents: dollarsToCents(
            spouseStateWithholdingInCents
          ),
          spouseIndividualIncomeInCents: dollarsToCents(
            spouseIndividualIncomeInCents
          ),
        })
      )

      if (res) {
        navigate('/practice/profile')
      }
    },
  })

  const { values, submitForm, isSubmitting, isValid } = formik

  const displaySpouse = shouldDisplaySpouse(values.filingStatus)
  const isScorp = values.taxEntityType === TAX_ENTITY_TYPES.form_1120_s

  // TODO: Replace Form with New UI skipcq: JS-0099
  return (
    <div className="flex flex-col gap-2 px-4">
      <Breadcrumb>
        <Breadcrumb.Item>
          <Breadcrumb.Link
            onClick={() => {
              navigate('/practice/profile')
            }}
          >
            <Icon icon={regular('arrow-left')} style={{ marginRight: 8 }} />
            Back to Practice Profile
          </Breadcrumb.Link>
        </Breadcrumb.Item>
      </Breadcrumb>
      <div className="h-4" />

      <div className="flex flex-row gap-2 items-center">
        <Icon
          icon={solid('bank')}
          backgroundStyle="rounded"
          backgroundColor="priority2"
          size="sm"
        />
        <Heading variant={HeadingVariants.H2}>
          {taxYear} Tax Information
        </Heading>
      </div>
      <Card>
        <FormikProvider value={formik}>
          <div className="flex flex-col space-y-4 gap-4">
            {error && <Alert variant="error" description={error.message} />}
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="flex-1">
                <Form.Select
                  name={getFieldName<typeof values>('filingStatus')}
                  label="Filing Status"
                  options={FilingStatusOptions}
                />
              </div>
              <div className="flex-1">
                <Form.Select
                  name={getFieldName<typeof values>('taxEntityType')}
                  label="Tax Entity Type"
                  options={TaxEntityTypeOptions}
                />
              </div>
            </div>

            <Form.RadioGroup
              name={getFieldName<typeof values>('itemizedDeduction')}
              label="Deduction Type"
            >
              <div className="flex flex-col gap-2">
                {[false, true].map((option) => (
                  <RadioGroupItem
                    key={option.toString()}
                    value={option.toString()}
                    label={option ? 'Itemized' : 'Standard'}
                  />
                ))}
              </div>
            </Form.RadioGroup>
            <div className="flex-1">
              <Form.Input
                name={getFieldName<typeof values>('numberOfDependents')}
                label="Number of Dependents"
              />
            </div>

            {isScorp && (
              <div className="flex-1">
                <Form.Input
                  name={getFieldName<typeof values>(
                    'scorpPracticeWIncomeInCents'
                  )}
                  label="YTD W-2 Private Practice"
                  componentType="currency"
                />
              </div>
            )}

            <div className="flex-1">
              <Form.Input
                name={getFieldName<typeof values>('wIncomeInCents')}
                label="YTD W-2 Income"
                componentType="currency"
              />
            </div>

            <div className="flex-1">
              <Form.Input
                name={getFieldName<typeof values>('federalWithholdingInCents')}
                label="YTD Federal Withholding"
                componentType="currency"
              />
            </div>

            <div className="flex-1">
              <Form.Input
                name={getFieldName<typeof values>('stateWithholdingInCents')}
                label="YTD State Withholding"
                componentType="currency"
              />
            </div>

            <div className="flex-1">
              <Form.Input
                name={getFieldName<typeof values>(
                  'otherIndividualIncomeInCents'
                )}
                label="YTD 1099 Income"
                componentType="currency"
              />
            </div>

            {displaySpouse && (
              <>
                <div className="flex-1">
                  <Form.Input
                    name={getFieldName<typeof values>('spouseFirstName')}
                    label="Spouse First Name"
                  />
                </div>

                <div className="flex-1">
                  <Form.Input
                    name={getFieldName<typeof values>('spouseLastName')}
                    label="Spouse Last Name"
                  />
                </div>

                <div className="flex-1">
                  <Form.Input
                    name={getFieldName<typeof values>('spouseWIncomeInCents')}
                    label="Spouse YTD W-2 Income"
                    componentType="currency"
                  />
                </div>

                <div className="flex-1">
                  <Form.Input
                    name={getFieldName<typeof values>(
                      'spouseFederalWithholdingInCents'
                    )}
                    label="Spouse YTD Federal Withholding"
                    componentType="currency"
                  />
                </div>

                <div className="flex-1">
                  <Form.Input
                    name={getFieldName<typeof values>(
                      'spouseStateWithholdingInCents'
                    )}
                    label="Spouse YTD State Withholding"
                    componentType="currency"
                  />
                </div>

                <div className="flex-1">
                  <Form.Input
                    name={getFieldName<typeof values>(
                      'spouseIndividualIncomeInCents'
                    )}
                    label="Spouse YTD 1099"
                    componentType="currency"
                  />
                </div>
              </>
            )}

            <div>
              <FileUploadFormInput
                label="Prior Year Tax Return"
                documentCat={UserDocumentCategoryIdentifier.finalReturn}
                taxYear={(taxYear - 1).toString()}
              />
            </div>

            <PracticeProfileFormCta
              submitForm={submitForm}
              isSubmitting={isSubmitting}
              isValid={isValid}
            />
          </div>
        </FormikProvider>
      </Card>
    </div>
  )
}
